/**
 * The four visibility levels of the media gallery as an ordered numeric rank, so that "at least as reachable as its
 * parent" is a single integer comparison. The numbers are stored, so they are spaced to leave room for a level
 * between two of them. Both the browser and the backend read them here, so a level means the same on both sides.
 *
 * Umbrella admins is the one level off that ladder: it reaches the admins of the organisations below, which
 * Organisation does not, and leaves out the own volunteers, which Organisation reaches. Every comparison of two levels
 * therefore goes through mediaVisibilityRankReaches rather than a bare >=.
 */
export const MEDIA_VISIBILITY_RANKS = {
  private: 10,
  umbrella_admins: 15,
  organisation: 20,
  umbrella: 30,
  link: 40,
}

// The safe thing is the default: a new folder reaches its own organisation and nothing beyond it
export const DEFAULT_MEDIA_VISIBILITY_RANK = MEDIA_VISIBILITY_RANKS.organisation

// Anything from here up leaves the owning organisation, which is what needs the consent of whoever is in the picture
export const CONSENT_REQUIRED_FROM_MEDIA_VISIBILITY_RANK = MEDIA_VISIBILITY_RANKS.umbrella

/**
 * Whether a level reaches everyone the required level reaches. On the ladder that is a plain >=, the only exception
 * being that Organisation does not reach the admins below that Umbrella admins reaches.
 */
export function mediaVisibilityRankReaches (rank, requiredRank) {
  return rank >= requiredRank &&
    !(requiredRank === MEDIA_VISIBILITY_RANKS.umbrella_admins && rank === MEDIA_VISIBILITY_RANKS.organisation)
}

// The levels a folder cannot be combined with above or below it, because neither of the two reaches all the other does
export function conflictingMediaVisibilityRanks (rank) {
  return Object.values(MEDIA_VISIBILITY_RANKS)
    .filter(other => !mediaVisibilityRankReaches(rank, other) && !mediaVisibilityRankReaches(other, rank))
}

// The translation key of a level, for a label as well as for the consent question that goes with it
export function mediaVisibilityKey (rank) {
  return `media_visibility_${Object.keys(MEDIA_VISIBILITY_RANKS).find(level => MEDIA_VISIBILITY_RANKS[level] === rank)}`
}

// The levels that widen to the organisations below, which an activity folder does not offer (see below)
export const NOT_INSIDE_ACTIVITY_MEDIA_VISIBILITY_RANKS = [MEDIA_VISIBILITY_RANKS.umbrella_admins, MEDIA_VISIBILITY_RANKS.umbrella]

/**
 * The levels an admin can pick for a folder or an item. Inside the folder of an activity taking part is what decides
 * who reaches it, and every participant of an activity holds an active dossier at the organisation that runs it, so
 * Organisation already reaches all of them. Organisation structure would only widen to people the participation
 * check keeps out anyway, which is a promise of access that is never kept - so it is not offered there, and neither is
 * Umbrella admins for the same reason.
 *
 * Private, Organisation and anyone with the link all keep their meaning inside an activity: private is the admins
 * alone, Organisation is the participants, and the link is its own address that participation never gates.
 */
export function allowedMediaVisibilityRanks (insideActivity) {
  return Object.values(MEDIA_VISIBILITY_RANKS)
    .filter(rank => !insideActivity || !NOT_INSIDE_ACTIVITY_MEDIA_VISIBILITY_RANKS.includes(rank))
}
